import { type SxProps, type Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

export const CALL_TRANSCRIPT_COPY_BUTTON_CLASS = 'call-transcript-copy-button';

const createCenteredSquareSx = (sizeRem: number): SxProps<Theme> => ({
  width: `${sizeRem}rem`,
  height: `${sizeRem}rem`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const createCenteredIconSx = (sizeRem: number): SxProps<Theme> => ({
  ...createCenteredSquareSx(sizeRem),
  fontSize: `${sizeRem}rem`,
  lineHeight: 1,
});

export const callTranscriptBoxSx: SxProps<Theme> = {
  borderRadius: 2,
  border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: (theme) => alpha(theme.palette.custom.OV.sys.surfaceContainer, 0.35),
  boxShadow: (theme) => theme.shadows[1],
  overflow: 'hidden',
};

export const CALL_TRANSCRIPT_HEADER_MIN_HEIGHT = '3.5rem';

export const callTranscriptHeaderSx = (showBottomBorder: boolean): SxProps<Theme> => ({
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 0,
  minHeight: CALL_TRANSCRIPT_HEADER_MIN_HEIGHT,
  borderBottom: (theme) => (showBottomBorder ? `1px solid ${theme.palette.divider}` : 'none'),
});

export const callTranscriptHeaderIconButtonSx: SxProps<Theme> = {
  p: 0,
  ...createCenteredSquareSx(1.75),
};

export const callTranscriptHeaderIconSx: SxProps<Theme> = createCenteredIconSx(1);

export const callTranscriptHeaderToggleIconSx: SxProps<Theme> = createCenteredIconSx(1.15);

export const callTranscriptMessagesContainerSx: SxProps<Theme> = {
  px: 1.5,
  pt: 1,
  pb: 2,
  gap: 1,
  maxHeight: '22rem',
  overflowY: 'auto',
};

export const callTranscriptMessagesWrapperSx: SxProps<Theme> = {
  position: 'relative',
};

export const callTranscriptResumeButtonSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: '0.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1,
  px: 1,
  py: 0.25,
  minWidth: 0,
  borderRadius: 100,
  textTransform: 'none',
  fontSize: '0.75rem',
  lineHeight: 1.4,
  boxShadow: (theme) => theme.shadows[2],
  backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
  color: (theme) => theme.palette.text.primary,
  '&:hover': {
    backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHighest,
  },
};

export const callTranscriptMessageContainerSx = (isAgent: boolean): SxProps<Theme> => ({
  alignSelf: isAgent ? 'flex-end' : 'flex-start',
  width: '100%',
  alignItems: isAgent ? 'flex-end' : 'flex-start',
  gap: 0.25,
});

export const callTranscriptMessageRowSx: SxProps<Theme> = {
  gap: 0.5,
  alignItems: 'center',
  maxWidth: '100%',
  [`& .${CALL_TRANSCRIPT_COPY_BUTTON_CLASS}`]: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [`&:hover .${CALL_TRANSCRIPT_COPY_BUTTON_CLASS}, &:focus-within .${CALL_TRANSCRIPT_COPY_BUTTON_CLASS}`]:
    {
      opacity: 1,
      pointerEvents: 'auto',
    },
};

export const callTranscriptMessageBubbleSx = (isAgent: boolean): SxProps<Theme> => ({
  px: 1,
  py: 0.75,
  borderRadius: 1.5,
  backgroundColor: (theme) =>
    isAgent ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.08),
  color: (theme) =>
    isAgent ? theme.palette.primary.contrastText : theme.palette.text.primary,
  maxWidth: '100%',
});

export const callTranscriptCopyButtonSx: SxProps<Theme> = {
  transition: 'opacity 120ms ease-in-out',
  p: 0,
  ...createCenteredSquareSx(1.5),
};

export const callTranscriptSearchBarSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  height: CALL_TRANSCRIPT_HEADER_MIN_HEIGHT,
  minHeight: CALL_TRANSCRIPT_HEADER_MIN_HEIGHT,
  flexShrink: 0,
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
};

export const callTranscriptSearchInputWrapSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  px: 1,
  height: '1.875rem',
  borderRadius: 1,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04),
  minWidth: 0,
};

export const callTranscriptSearchInputSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
  fontSize: '0.875rem',
  '& .MuiInputBase-input': {
    p: 0,
  },
};

export const callTranscriptSearchCountSx = (hasResults: boolean): SxProps<Theme> => ({
  fontSize: '0.75rem',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  color: (theme) => (hasResults ? theme.palette.text.secondary : theme.palette.error.main),
});
